<template>
  <v-list class="courses-list py-0">
    <div
      :is="activeComponent"
      v-model="treeItems"
      :group="{ name: 'coursesTree' }"
      class="draggable"
      @start="drag = true"
      @end="drag = false"
      @change="onChange"
    >
      <div v-for="(item, index) in treeItems" :key="item.id">
        <v-list-item class="hoverable">
          <v-list-item-icon class="mr-2">
            <v-btn
              icon
              :class="{ hidden: !item.children || activeComponent !== 'draggable' }"
              @click="toggleShowSubElements(item)"
              ><v-icon>{{ item.showSubElements ? 'mdi-menu-down' : 'mdi-menu-right' }}</v-icon></v-btn
            >
            <v-btn :ripple="false" icon class="no-active" @click="onItemEdit(item, index)"
              ><v-icon>{{ item.icon }}</v-icon></v-btn
            >
          </v-list-item-icon>

          <v-list-item-content class="item" @click="onItemEdit(item, index)">
            <v-list-item-title>{{ item.name }}<strong v-if="item.isRequired"> *</strong></v-list-item-title>
          </v-list-item-content>

          <v-list-item-action v-if="activeComponent === 'draggable'">
            <v-dialog v-model="deleteDialogs[item.id]" persistent :max-width="item.isModule ? 500 : 350">
              <template v-slot:activator="{ on }">
                <v-btn icon v-on="on"><v-icon color="error">mdi-delete</v-icon></v-btn>
              </template>
              <v-card>
                <v-toolbar color="error" dark flat>
                  <v-toolbar-title>Ostrzeżenie</v-toolbar-title>
                </v-toolbar>
                <v-card-text class="mt-4">
                  <p>Czy na pewno chcesz usunąć ten element?</p>
                  <p v-if="item.isModule">
                    Usunięcie modułu spowoduje usunięcie też jego podelementów.
                  </p>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="primary" text @click="deleteDialogs[item.id] = false">Nie</v-btn>
                  <v-btn color="primary" text @click="removeNode(item.id, index)">Tak</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-list-item-action>
        </v-list-item>

        <EditableCourseTree
          v-if="item.isModule"
          :style="{ display: item.showSubElements ? 'block' : 'none' }"
          :items="item.children || []"
          :nest-level="nestLevel + 1"
          :nest-order="index"
          :path="[...path, index]"
          :course-id="courseId"
          :parent-id="item.id"
        />

        <v-dialog v-model="loaderDialog" width="400">
          <v-card color="primary" dark>
            <v-card-text>
              <p class="pt-4">
                Aktualizacja drzewka elementów, proszę czekać...
              </p>
              <v-progress-linear indeterminate color="white"></v-progress-linear>
            </v-card-text>
          </v-card>
        </v-dialog>
      </div>
    </div>
  </v-list>
</template>

<script>
import draggable from 'vuedraggable';
import { createNamespacedHelpers } from 'vuex';

const { mapActions } = createNamespacedHelpers('courses');

export default {
  name: 'EditableCourseTree',
  components: {
    draggable
  },
  props: {
    items: {
      type: Array,
      required: true
    },
    nestLevel: {
      type: Number,
      default: 0
    },
    nestOrder: {
      type: Number,
      default: 0
    },
    path: {
      type: Array,
      default: () => []
    },
    courseId: {
      type: Number,
      required: true
    },
    parentId: {
      validator: prop => typeof prop === 'number' || prop === null,
      required: true
    }
  },
  data: function() {
    return {
      drag: false,
      deleteDialogs: this.items.reduce((map, element) => {
        map[element.id] = false;
        return map;
      }, {}),
      loaderDialog: false
    };
  },
  computed: {
    treeItems: {
      get() {
        return this.items;
      },
      set() {
        // items are updated in store
      }
    },
    activeComponent() {
      return this.nestLevel === 0 && this.items.length ? 'div' : 'draggable';
    }
  },
  watch: {
    items() {
      this.showSubElements = this.items.reduce((map, element) => {
        map[element.id] = true;
        return map;
      }, {});
      this.deleteDialogs = this.items.reduce((map, element) => {
        map[element.id] = false;
        return map;
      }, {});
    }
  },
  methods: {
    ...mapActions(['updateTree', 'requestElementEdit', 'removeElement', 'removeCourseElement', 'toggleShowElements']),
    onItemEdit(element, index) {
      const type = element.isModule
        ? 'module'
        : element.type === 1
        ? 'lesson'
        : element.type === 2
        ? 'video'
        : element.type === 3
        ? 'file'
        : element.type === 4
        ? 'meeting'
        : undefined;
      if (type === undefined) throw 'Unknow item type';
      this.requestElementEdit({
        type: type,
        element: element,
        path: [...this.path, index]
      });
    },
    async onChange(ev) {
      if (ev.toString() === '[object CustomEvent]') {
        return;
      }

      this.loaderDialog = true;
      await this.updateTree({
        action: ev,
        nestLevel: this.nestLevel,
        nestOrder: this.nestOrder,
        path: this.path,
        courseId: this.courseId,
        parentId: this.parentId
      });
      await new Promise(resolve => setTimeout(() => resolve(), 1)); // TODO?: quick fix for bug with not hiding modal
      this.loaderDialog = false;
    },
    async removeNode(id, index) {
      this.loaderDialog = true;
      this.deleteDialogs[id] = false;
      await this.removeCourseElement({
        element: this.treeItems[index],
        courseId: this.courseId
      });
      this.loaderDialog = false;
    },
    toggleShowSubElements(item) {
      this.toggleShowElements({ path: [...this.path, this.treeItems.findIndex(el => el.id === item.id)] });
    }
  }
};
</script>

<style lang="scss" scoped>
.no-active {
  &:hover,
  &:focus {
    &:before {
      opacity: 0 !important;
    }
  }
}

.hidden {
  visibility: hidden;
  cursor: default;
}

.draggable {
  outline: 1px solid rgba(0, 0, 0, 0.12);
  min-height: 68px;
}

.hoverable {
  padding-left: 4px;
  padding-right: 4px;

  .item {
    cursor: pointer;
  }
}

@mixin nestingPadding($step: 32px, $maxStep: 192px) {
  .courses-list .courses-list {
    padding-left: $step;
    @if $maxStep >= $step {
      // exit
    } @else {
      @include opacityStair($step * 2, $maxStep);
    }
  }
}

/* more nesting? */
@include nestingPadding(32px, 640px);
</style>
